/**
 * Runtime configuration.
 *
 * Deliberately fetched at boot from `/config.json` rather than baked in as
 * VITE_* build-time constants. The three SPAs ship as immutable container
 * images; repointing them at a different Keycloak or realm should not require
 * rebuilding and redeploying three images. The router serves one config.json
 * for the whole platform, so there is exactly one file to change.
 *
 * Path is absolute on purpose: /crm/ and /admin/ are served under a base path,
 * but the config lives at the origin root for all of them.
 */

export interface PlatformConfig {
  /** Keycloak base URL, no trailing slash, e.g. https://id.example.com */
  keycloakUrl: string;
  /** Realm name, e.g. "platform" */
  realm: string;
  /** Public PKCE client shared by every SPA. */
  clientId: string;
  /** Hub API prefix as seen from the browser. Same origin, so no CORS. */
  apiBase: string;
  /** Shown in the header; lets one image serve more than one company later. */
  companyName: string;
}

const CONFIG_URL = "/config.json";

let cached: Promise<PlatformConfig> | null = null;

function validate(raw: unknown): PlatformConfig {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("config.json is not an object");
  }
  const c = raw as Record<string, unknown>;
  const required = ["keycloakUrl", "realm", "clientId", "apiBase"] as const;
  for (const key of required) {
    if (typeof c[key] !== "string" || (c[key] as string).length === 0) {
      throw new Error(`config.json is missing "${key}"`);
    }
  }
  return {
    keycloakUrl: (c.keycloakUrl as string).replace(/\/+$/, ""),
    realm: c.realm as string,
    clientId: c.clientId as string,
    apiBase: (c.apiBase as string).replace(/\/+$/, ""),
    companyName: typeof c.companyName === "string" ? c.companyName : "CQT Ammo",
  };
}

/** Loads (and caches) the platform config. Rejects loudly — a bad config means
 *  login cannot work, and a silent default would be worse than an error page. */
export function loadConfig(): Promise<PlatformConfig> {
  if (!cached) {
    cached = fetch(CONFIG_URL, { cache: "no-store" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`GET ${CONFIG_URL} -> ${res.status}`);
        return validate(await res.json());
      })
      .catch((err) => {
        cached = null; // allow a retry
        throw err;
      });
  }
  return cached;
}
